/**
 * The account categories a project config can be created under.
 *
 * An entry marked `legacy: true` is kept so existing rows still resolve their label, but is not
 * offered when creating something new. Use {@link selectableProjectConfigType} for a create
 * picker, and {@link isSelectableProjectConfigType} for an edit picker, which must still show a
 * legacy value when that is what the row already holds.
 */
export const projectConfigType = [
  {
    key: "SALES",
    label: "SALES",
    tooltip: "sale masuk, contoh: jualan harian",
  },
  {
    key: "INCOME OTHER",
    label: "OTHER INCOME",
    tooltip:
      "Penjualan barang bukan niche company: cth: kereta company atau duit masuk dari entity luar secara sukarela",
  },
  {
    key: "PURCHASE",
    label: "PURCHASE",
    tooltip: "stock/barang mentah dibeli untuk perjualan produk.",
  },
  {
    key: "EXPENSES",
    label: "EXPENSES",
    tooltip: "cost utilities, gaji pekerja, marketing, operation",
  },
  {
    /*
     * Retired for NEW entries the day Refund Notes shipped.
     *
     * A REFUND category is a standalone outbound transaction with no link back to any invoice,
     * so the invoice it relates to still reads as fully billed while the money has already gone.
     * Refunding an invoice is now a Refund Note against that invoice, and reducing one that has
     * not been paid is a Credit Note. Leaving both routes on offer is the double-count risk the
     * whole feature exists to remove: the same return recorded twice, once as a note and once as
     * a REFUND transaction, with nothing tying them together.
     *
     * The entry STAYS here, and that is deliberate. Existing REFUND categories and every
     * transaction posted against them are untouched, still readable, and still aggregated onto
     * the refund line of the income statement -- history is never rewritten. What `legacy` does
     * is stop the category being offered for something new: the create screens filter it out,
     * and the edit screens offer it only to a row that already is one, so nothing can be
     * switched INTO it.
     *
     * Note this is NOT the deposit refund. Handing back money held against no invoice is a
     * different mechanism entirely -- DepositServiceImpl.refund() and its own screen -- and it
     * is unaffected.
     */
    key: "REFUND",
    label: "REFUND (LEGACY)",
    legacy: true,
    tooltip:
      "Nota Bayaran Balik menggantikan ini. Kategori lama kekal boleh dibaca, tetapi tiada kategori REFUND baharu boleh dibuat.",
  },
  {
    // Money that has moved before the sale it belongs to. Both carry the DEPOSIT cost type, and
    // the backend derives that plus the debit side from this key alone -- so the two deposit
    // screens had everything they needed EXCEPT a way for anyone to create the category.
    key: "DEPOSIT RECEIVED",
    label: "CUSTOMER DEPOSIT",
    tooltip:
      "deposit diterima dari pelanggan sebelum jualan. Bukan pendapatan lagi -- wang masih terhutang kepada pelanggan sehingga invois dikeluarkan",
  },
  {
    key: "DEPOSIT PAID",
    label: "SUPPLIER ADVANCE",
    tooltip:
      "pendahuluan dibayar kepada pembekal sebelum barang diterima. Bukan kos lagi -- ia aset sehingga bil pembekal dikeluarkan",
  },
  { key: "CASHOUT", label: "DIVIDEND PAYMENT", tooltip: "ambilan" },
  {
    key: "CAPITAL EQUITY",
    searchKey: "CAPITALSpAcEEQUITY",
    label: "CAPITAL",
    tooltip: "modal pemilik",
  },
  {
    key: "CAPITAL EQUITY REPAYMENT",
    searchKey: "CAPITALSpAcEEQUITYSpAcEREPAYMENT",
    label: "CAPITAL REPAYMENT",
    tooltip: "modal pemilik (bayaran balik)",
  },
  {
    key: "CAPITAL DEPTH",
    label: "BUSINESS LOAN",
    tooltip: "duit masuk dari pinjaman perniagaan",
  },
  {
    key: "CAPITAL DEPTH REPAYMENT",
    label: "BUSINESS LOAN REPAYMENT",
    tooltip: "bayaran balik pinjaman perniagaan",
  },
  {
    key: "CAPITAL DEPTH CREDIT",
    label: "BUSINESS LOAN (OUT)",
    tooltip: "Memberi pinjaman perniagaan",
  },
  {
    key: "CAPITAL DEPTH CREDIT REPAYMENT",
    label: "BUSINESS LOAN (OUT) REPAYMENT",
    tooltip: "bayaran balik dari peminjam",
  },
  {
    key: "ASSET FIXED",
    label: "FIXED ASSET",
    tooltip: "rumah, mesin, tanah & etc",
  },
  {
    key: "ASSET CURRENT",
    label: "CURRENT ASSET",
    tooltip: "asset yang senang dicairkan. cth: cash, stock, inventory & etc",
  },
  {
    key: "OTHERS",
    label: "NON-DEDUCTIBLE EXPENSES",
    tooltip:
      "cost yang tidak termasuk dalam pengiraan tax. contoh: cost entertainment",
  },
  {
    key: "TRANSFER DEBIT",
    label: "TRANFER IN",
    tooltip: "akaun tranfer duit masuk dari bank lain",
  },
  {
    key: "TRANSFER CREDIT",
    label: "TRANSFER OUT",
    tooltip: "akaun tranfer keluar ke bank lain",
  },
];

/**
 * The categories offered when creating a new account or template.
 *
 * Retired categories are absent. Reading one still works everywhere -- lists, filters, reports
 * and the income statement all keep their entry -- but nothing new can be filed under it.
 */
export const selectableProjectConfigType = projectConfigType.filter(
  (configType) => !configType.legacy
);

/**
 * Whether an edit picker should offer this category.
 *
 * A legacy category is offered only when it is what the row already holds, so an existing REFUND
 * account still renders as REFUND and can be edited -- but no other account can be switched into
 * it. Dropping it outright would silently re-file the row under whichever option happened to be
 * first in the list.
 */
export const isSelectableProjectConfigType = (configType, currentValue) =>
  !configType.legacy || configType.key === currentValue;
